import { useCallback, useEffect, useRef, useState } from "react"
import Placeholder from "@tiptap/extension-placeholder"
import { Editor } from "@tiptap/react"
import { find } from "linkifyjs"
import { useDebounce } from "react-use"

import {
	cn,
	getClipboardFiles,
	getDefaultExtensions,
} from "@productlane/lib/src/browser"
import { typographyVariants } from "@productlane/ui"
import CustomCodeBlock, {
	useAllCodeBlockLanguages,
} from "@productlane/ui/src/components/editor/extenstions/custom-code-block"
import CustomInlineCode from "@productlane/ui/src/components/editor/extenstions/custom-inline-code"
import CustomLink from "@productlane/ui/src/components/editor/extenstions/custom-link"
import { mutators } from "@productlane/zero/mutators"

import type { PendingNoteSave } from "./thread-note-save"
import type { EditorView } from "@tiptap/pm/view"
import {
	DEFAULT_EDITOR_PROPS,
	useHighlights,
} from "@/components/editor/editor-config"
import { EmojiSuggestion } from "@/components/editor/extensions/emoji"
import { insertMarkdownPaste } from "@/components/editor/markdown-paste"
import { toast } from "@/components/toast"
import { ALLOWED_FILE_SIZE_IN_MB } from "@/constants"
import { api } from "@/lib/api"
import { useZero } from "@/lib/zero"
import { useThreadContext } from "../thread-context"
import {
	capturePendingNoteSave,
	markPendingNoteSaved,
	resolvePendingNoteSave,
} from "./thread-note-save"
import { useEditorLinks } from "./useEditorLinks"

// Marks programmatic transactions (autolinking) so they never trigger a save.
const LINKIFY_TRANSACTION_META = "threadNoteLinkify"

export function useThreadEditor(isEditable?: boolean, enabled = true) {
	const { thread } = useThreadContext()
	const zero = useZero()
	const { setHighlights } = useHighlights()
	const { extractAndUpdateLinks } = useEditorLinks()

	// Track if local changes are in progress to prevent external sync overwriting
	const isLocalChangeRef = useRef(false)
	// Store initial content to avoid recreating editor on every thread.text change
	const initialContentRef = useRef(thread.text)

	// Debounced save for note text
	const [pendingSave, setPendingSave] = useState<PendingNoteSave | null>(null)

	useDebounce(
		() => {
			const save = resolvePendingNoteSave(pendingSave)
			if (save) {
				void zero.mutate(mutators.threads.update(save))
				setPendingSave((prev) => markPendingNoteSaved(prev, save))
				// Reset local change flag after save completes
				isLocalChangeRef.current = false
			}
		},
		500,
		[pendingSave],
	)
	const getFileUrlMutation = api.file.getUrl.useMutation()
	const uploadImage = useCallback(
		async (file: File, view: EditorView) => {
			const { uploadUrl, fileUrl } = await getFileUrlMutation.mutateAsync({
				fileName: file.name,
				fileType: file.type,
			})
			const filesize = (file.size / 1024 / 1024).toFixed(4) // get the filesize in MB
			if (Number(filesize) < ALLOWED_FILE_SIZE_IN_MB) {
				const headers = { "Content-Type": file.type }
				const res = await fetch(uploadUrl, {
					method: "PUT",
					body: file,
					headers,
				})
				if (!res.ok) {
					throw new Error("File upload failed")
				}

				if (view) {
					const node = view.state.schema.nodes.image?.create({
						src: fileUrl,
					})
					if (!node) {
						throw new Error("Failed to create image node")
					}
					const transaction = view.state.tr.replaceSelectionWith(node)
					view.dispatch(transaction)
					toast.success({ title: "Image uploaded" })
				}
			} else {
				toast.error({
					title: `Images need to be in jpg or png format and less than ${ALLOWED_FILE_SIZE_IN_MB}mb in size.`,
				})
			}
		},
		[getFileUrlMutation],
	)
	// Ref for onUpdate so the editor callback always uses fresh values
	const onUpdateRef = useRef(
		(_ed: {
			isDestroyed: boolean
			isFocused: boolean
			getHTML: () => string
		}) => {
			// replaced below on every render with a closure over fresh values
		},
	)
	onUpdateRef.current = (ed) => {
		if (!ed.isDestroyed) {
			isLocalChangeRef.current = true
			const htmlContent = ed.getHTML()
			extractAndUpdateLinks(htmlContent)
			setPendingSave((prev) =>
				capturePendingNoteSave(thread, htmlContent, prev),
			)
			setTimeout(() => setHighlights(), 0)
		}
	}

	// Thread note editor — only created when enabled
	const [editor, setEditor] = useState<Editor | null>(null)
	const editorRef = useRef<Editor | null>(null)

	useEffect(() => {
		if (!enabled) return

		const extensions = [
			...getDefaultExtensions({
				image: true,
				history: true,
				link: false,
			}),
			EmojiSuggestion,
			Placeholder.configure({
				placeholder: "Link multiple requests from meeting notes to Linear",
			}),
			CustomInlineCode,
			CustomCodeBlock,
			CustomLink.configure({
				borderColor: "var(--portal-accent-color)",
			}),
		]

		const instance = new Editor({
			content: initialContentRef.current,
			editable: isEditable ?? true,
			extensions,
			onCreate({ editor: ed }) {
				if (!ed.isDestroyed) {
					setTimeout(() => setHighlights(), 0)
				}
			},
			onUpdate({ editor: ed, transaction }) {
				if (transaction.getMeta(LINKIFY_TRANSACTION_META)) return
				onUpdateRef.current(ed)
			},
			editorProps: {
				...DEFAULT_EDITOR_PROPS,
				attributes: {
					...DEFAULT_EDITOR_PROPS.attributes,
					class: cn(
						"focus:outline-none",
						typographyVariants({ variant: "message", mode: "invert" }),
					),
					id: "inner-editor",
				},
				handleDrop: (view, event, _slice, moved) => {
					if (!moved && event.dataTransfer?.files?.[0]) {
						const item = event.dataTransfer.files[0]
						if (item.type.startsWith("image")) {
							void uploadImage(item, view)
							return true
						}
					}
					return false
				},
				handlePaste: (view, event) => {
					const image = getClipboardFiles(event.clipboardData?.items).find(
						(file) => file.type.startsWith("image"),
					)
					if (image) {
						void uploadImage(image, view)
						return true
					}
					return insertMarkdownPaste({
						view,
						event,
						editor: editorRef.current,
						extensions,
					})
				},
				transformPastedHTML(html) {
					return html.replace(/<img.*?>/g, "")
				},
			},
		})
		editorRef.current = instance
		setEditor(instance)

		return () => {
			instance.destroy()
			editorRef.current = null
			setEditor(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled])

	// Reset the local-change flag when switching threads. Without this, a
	// pending draft on thread A would block syncing thread B's note content
	// into the (persistent) editor after we dropped the remount key.
	useEffect(() => {
		isLocalChangeRef.current = false
	}, [thread.id])

	// Sync external changes to editor content (e.g., from another user,
	// another source, or a thread switch).
	useEffect(() => {
		if (!editor || editor.isDestroyed) return

		// Skip if we have local changes in progress or editor is focused
		if (isLocalChangeRef.current || editor.isFocused) return

		// Check if external content differs from current editor content
		if (thread.text !== editor.getHTML()) {
			editor.commands.setContent(thread.text ?? "")
		}
	}, [thread.id, thread.text, editor])

	// Convert plain text URLs to links when editor content is loaded
	useEffect(() => {
		if (!editor) return

		// Wait a bit for content to load
		const timeout = setTimeout(() => {
			// Get all text nodes in the document
			const { state } = editor
			const { tr } = state
			let modified = false

			state.doc.descendants((node, pos) => {
				if (!node.isText || !node.text) return

				// Find URLs in the text
				const links = find(node.text)
				if (links.length === 0) return

				// Check if this text already has link marks
				const marks = node.marks
				if (marks.some((mark) => mark.type.name === "link")) return

				// Add link marks for each URL found
				links.forEach((link) => {
					if (link.isLink) {
						const from = pos + link.start
						const to = pos + link.end
						const linkMark = state.schema.marks.link?.create({
							href: link.href,
						})
						if (linkMark) {
							tr.addMark(from, to, linkMark)
							modified = true
						}
					}
				})
			})

			if (modified) {
				// Use the meta flag to prevent autolink from running
				tr.setMeta("addToHistory", false)
				tr.setMeta(LINKIFY_TRANSACTION_META, true)
				editor.view.dispatch(tr)
			}
		}, 500)

		return () => clearTimeout(timeout)
	}, [editor])

	useAllCodeBlockLanguages(editor)

	return { editor }
}
